/**
 * The arithmetic behind "extend this deposit hold" (CAP-1286).
 *
 * The hold must still be live at an instant: the follow-on stay's checkout plus a
 * buffer for inspecting the house. A checkout is a bare date here, so it is anchored
 * at the end of that day in UTC, the same conservative anchor admin-api gives a bare
 * checkout date: it can only make the hold last slightly longer, never release it
 * before the guest has left.
 */

export const BUFFER_CHOICES_HOURS = [24, 48, 72] as const
export const DEFAULT_BUFFER_HOURS = 48

const HOUR_MS = 60 * 60 * 1000
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/** The ISO instant a hold must stay live through, or null for anything that is not a date. */
export function holdUntilFor(
  checkOutDate: string | null | undefined,
  bufferHours: number
): string | null {
  const day = checkOutDate?.slice(0, 10) ?? ''
  if (!ISO_DATE.test(day)) return null
  const end = Date.parse(`${day}T23:59:59.999Z`)
  if (!Number.isFinite(end)) return null
  return new Date(end + bufferHours * HOUR_MS).toISOString()
}

/** How a bare stay date reads: "Nov 1". */
export function shortDate(value: string | null | undefined): string {
  const day = value?.slice(0, 10) ?? ''
  if (!ISO_DATE.test(day)) return ''
  return new Date(`${day}T12:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

interface StayLike {
  id?: string | number
  reservation_id?: string | number
  status?: string
  check_in: string | null
  check_out: string | null
}

/**
 * Bookings that look like the continuation of a stay: same property (the caller's list),
 * not cancelled, starting within a day of this one's checkout, and ending after it.
 * Nearest start first.
 */
export function followOnCandidates<T extends StayLike>(
  stays: readonly T[],
  thisId: string,
  thisCheckOut: string | null | undefined
): T[] {
  const out = thisCheckOut?.slice(0, 10) ?? ''
  if (!ISO_DATE.test(out)) return []
  const outMs = Date.parse(`${out}T00:00:00Z`)
  return stays
    .filter(stay => {
      const id = String(stay.id ?? stay.reservation_id ?? '')
      if (!id || id === thisId) return false
      if (stay.status && /cancel/i.test(stay.status)) return false
      const start = stay.check_in?.slice(0, 10) ?? ''
      const end = stay.check_out?.slice(0, 10) ?? ''
      if (!ISO_DATE.test(start) || !ISO_DATE.test(end)) return false
      const startMs = Date.parse(`${start}T00:00:00Z`)
      return Math.abs(startMs - outMs) <= 24 * HOUR_MS && end > out
    })
    .sort((a, b) => (a.check_in ?? '').localeCompare(b.check_in ?? ''))
}
